import { createClient, type SupabaseClient } from '@supabase/supabase-js'
import { GUEST_MODE } from '@/guest/guestMode'
import {
    IMPERSONATION_AUTH_STORAGE_KEY,
    isImpersonatingClient,
} from '@/utils/impersonationHeaders'

const supabaseUrl = import.meta.env.VITE_APP_SUPABASE_URL
const supabaseAnonKey = import.meta.env.VITE_APP_SUPABASE_PUBLIC_KEY

/**
 * Inert Supabase facade for guest mode — never touches the network or
 * localStorage. Callers get benign empty shapes so unguarded paths degrade
 * quietly instead of throwing.
 */
function createGuestSupabaseFacade(): SupabaseClient {
    const emptyResult = {
        data: null,
        error: null,
        count: null,
        status: 200,
        statusText: 'OK',
    }
    const emptyList = {
        data: [],
        error: null,
        count: 0,
        status: 200,
        statusText: 'OK',
    }

    const thenable = (value: any) => ({
        then: (resolve: any) => Promise.resolve(value).then(resolve),
        catch: (reject: any) => Promise.resolve(value).catch(reject),
        finally: (fn: any) => Promise.resolve(value).finally(fn),
    })

    const queryBuilder: any = new Proxy(
        {},
        {
            get(_t, prop) {
                if (prop === 'then') {
                    return (resolve: any) => Promise.resolve(emptyList).then(resolve)
                }
                return () => queryBuilder
            },
        },
    )

    const channelStub: any = {
        on() {
            return channelStub
        },
        subscribe() {
            return channelStub
        },
        unsubscribe() {
            return Promise.resolve('ok')
        },
        send() {
            return Promise.resolve('ok')
        },
        track() {
            return Promise.resolve('ok')
        },
        untrack() {
            return Promise.resolve('ok')
        },
    }

    const storageBucket: any = {
        upload: () => thenable({ data: null, error: null }),
        download: () => thenable({ data: null, error: null }),
        list: () => thenable({ data: [], error: null }),
        remove: () => thenable({ data: [], error: null }),
        createSignedUrl: () =>
            thenable({ data: { signedUrl: '' }, error: null }),
        getPublicUrl: () => ({ data: { publicUrl: '' } }),
    }

    const facade: any = {
        auth: {
            getSession: () =>
                Promise.resolve({ data: { session: null }, error: null }),
            getUser: () =>
                Promise.resolve({ data: { user: null }, error: null }),
            onAuthStateChange: () => ({
                data: { subscription: { unsubscribe() {} } },
            }),
            signOut: () => Promise.resolve({ error: null }),
            signInWithPassword: () =>
                Promise.resolve({ data: { user: null, session: null }, error: null }),
            signInWithOAuth: () =>
                Promise.resolve({ data: { provider: null, url: null }, error: null }),
            signUp: () =>
                Promise.resolve({ data: { user: null, session: null }, error: null }),
            refreshSession: () =>
                Promise.resolve({ data: { session: null }, error: null }),
            setSession: () =>
                Promise.resolve({ data: { session: null }, error: null }),
            updateUser: () =>
                Promise.resolve({ data: { user: null }, error: null }),
            resetPasswordForEmail: () => Promise.resolve({ data: {}, error: null }),
            resend: () => Promise.resolve({ data: {}, error: null }),
        },
        from: () => queryBuilder,
        rpc: () => thenable(emptyResult),
        storage: {
            from: () => storageBucket,
        },
        channel: () => channelStub,
        removeChannel: () => Promise.resolve('ok'),
        getChannels: () => [],
        removeAllChannels: () => Promise.resolve([]),
        functions: {
            invoke: () => thenable({ data: null, error: null }),
        },
        realtime: {
            connect() {},
            disconnect() {},
            setAuth() {},
        },
    }

    return facade as SupabaseClient
}

/**
 * Impersonation must not share the default localStorage session. `setSession`
 * persists tokens and BroadcastChannels a SIGNED_IN to every tab on the same
 * storage key — that is what kicked the dev harness to /404 (it started
 * seeing the impersonated user, who is not allowlisted).
 *
 * Decided at module init from the tab's path / sessionStorage flag, so a
 * Lightbase handoff (full load of `/impersonate`) and later reloads of that
 * tab stay isolated. Other tabs keep the staff session in localStorage.
 */
export const isImpersonationAuthIsolated = !GUEST_MODE && isImpersonatingClient()

/**
 * The desktop app's sign-in bridge (`/auth/callback?client=desktop`, opened in
 * the system browser) only forwards the tokens to the app. Signing this
 * browser in with them too would share one refresh token between two clients,
 * and whichever refreshed second would be rejected (and sign the app out).
 */
const isDesktopAuthBridgePage = typeof window !== 'undefined'
    && window.location.pathname === '/auth/callback'
    && new URLSearchParams(window.location.search).get('client') === 'desktop'

export const supabase: SupabaseClient = GUEST_MODE
    ? createGuestSupabaseFacade()
    : createClient(supabaseUrl, supabaseAnonKey, {
          auth: {
              autoRefreshToken: true,
              persistSession: true,
              // Handoff tokens live in the hash for consumeHandoffFromUrl;
              // do not let GoTrue treat `/impersonate#access_token=` as OAuth.
              detectSessionInUrl: !isImpersonationAuthIsolated && !isDesktopAuthBridgePage,
              storage: isImpersonationAuthIsolated ? window.sessionStorage : window.localStorage,
              ...(isImpersonationAuthIsolated
                  ? { storageKey: IMPERSONATION_AUTH_STORAGE_KEY }
                  : {}),
          },
      })
